import { IUserInfoDto } from "@kuika/kuika-cl-model";

/**
 * The accounts that have signed in on this browser, so the sign-in page can greet a returning
 * person with "Welcome back" instead of an empty form.
 *
 * Deliberately NOT removed by `LocalStorageService.clearToken()`: a sign-out, a session that could
 * not be renewed and a server-forced logout all leave the list in place, which is the point — the
 * page that follows is exactly where the list is shown. The only way an entry leaves is the
 * remove control in the account chooser, or being pushed out by a newer sign-in past the cap.
 *
 * Holds identity for display only — no token. The profile photo URL is kept because the photo is
 * uploaded as a PUBLIC storage object (`UserService.UpdateUserProfilePhoto`), so it loads without the
 * session that is gone by the time this is read; the avatar falls back to initials when it does not.
 */
export const REMEMBERED_ACCOUNTS_STORAGE_KEY: string = "kuika-remembered-accounts";

export const MAX_REMEMBERED_ACCOUNTS: number = 3;

const STORE_VERSION: number = 1;

export type RememberedSignInMethod = "password" | "google";

export interface IRememberedAccount {
  email: string;
  firstName: string;
  lastName: string;
  /** How this person signed in last time; the account step offers it first. */
  method: RememberedSignInMethod;
  /** Epoch ms. Orders the list, most recent first. */
  lastSignedInAt: number;
  /** Public profile photo. Absent when the person has none, or on entries written before photos were kept. */
  photoUrl?: string;
}

interface IRememberedAccountsStore {
  v: number;
  accounts: IRememberedAccount[];
}

interface IStoreRead {
  accounts: IRememberedAccount[];
  /** A well-formed store written by another version. Neither read nor overwritten by this build. */
  isForeign: boolean;
}

/** One account per address, however it was typed: `Ayse@Acme.com` and `ayse@acme.com` are one person. */
export const normaliseRememberedEmail = (email: string): string => email.trim().toLowerCase();

const isRememberedAccount = (value: unknown): value is IRememberedAccount => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate: Partial<IRememberedAccount> = value as Partial<IRememberedAccount>;
  return (
    typeof candidate.email === "string" &&
    candidate.email.trim().length > 0 &&
    typeof candidate.firstName === "string" &&
    typeof candidate.lastName === "string" &&
    (candidate.method === "password" || candidate.method === "google") &&
    typeof candidate.lastSignedInAt === "number" &&
    Number.isFinite(candidate.lastSignedInAt) &&
    (candidate.photoUrl === undefined || typeof candidate.photoUrl === "string")
  );
};

/**
 * Only an absolute http(s) address is kept. The stored value ends up in an `<img src>` on the
 * sign-in page, and a `data:`/`javascript:` value or a relative path has no business there.
 */
const toPhotoUrl = (value: string | undefined): string | undefined => {
  const trimmed: string = value?.trim() ?? "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : undefined;
};

const byMostRecent = (a: IRememberedAccount, b: IRememberedAccount): number => b.lastSignedInAt - a.lastSignedInAt;

const removeStore = (): void => {
  try {
    window.localStorage.removeItem(REMEMBERED_ACCOUNTS_STORAGE_KEY);
  } catch {
    // Storage refused (private mode, blocked site data): there is nothing to remove either.
  }
};

const writeStore = (accounts: IRememberedAccount[]): void => {
  if (accounts.length === 0) {
    removeStore();
    return;
  }
  const store: IRememberedAccountsStore = { v: STORE_VERSION, accounts };
  try {
    window.localStorage.setItem(REMEMBERED_ACCOUNTS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota or blocked storage. Remembering is a convenience; signing in must not fail over it.
  }
};

const EMPTY_READ: IStoreRead = { accounts: [], isForeign: false };

/**
 * The stored value is untrusted — another build, a hand edit or a truncated write may have left
 * anything there — so it is parsed as `unknown` and every entry is shape-checked. A value that is
 * not JSON is removed. A well-formed store of another version is reported as foreign: a newer build
 * (or an older one, after a rollback) owns it, so this build neither reads nor overwrites it.
 */
const readStore = (): IStoreRead => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(REMEMBERED_ACCOUNTS_STORAGE_KEY);
  } catch {
    return EMPTY_READ;
  }
  if (raw === null) {
    return EMPTY_READ;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeStore();
    return EMPTY_READ;
  }
  if (typeof parsed !== "object" || parsed === null) {
    removeStore();
    return EMPTY_READ;
  }

  const store: Partial<IRememberedAccountsStore> = parsed as Partial<IRememberedAccountsStore>;
  if (store.v !== STORE_VERSION) {
    return { accounts: [], isForeign: true };
  }
  if (!Array.isArray(store.accounts)) {
    removeStore();
    return EMPTY_READ;
  }

  const entries: unknown[] = store.accounts;
  const accounts: IRememberedAccount[] = entries.filter(isRememberedAccount);
  return { accounts: [...accounts].sort(byMostRecent).slice(0, MAX_REMEMBERED_ACCOUNTS), isForeign: false };
};

/** The remembered accounts, most recent first, never more than `MAX_REMEMBERED_ACCOUNTS`. */
export const readRememberedAccounts = (): IRememberedAccount[] => readStore().accounts;

/**
 * Puts the person who just signed in at the top of the list, replacing any older entry for the
 * same address and dropping the oldest past the cap.
 *
 * Call it only where a person has just CHOSEN to sign in on this device — the sign-in form and the
 * Google button. Not from `UserService.Login` itself (the profile's change-password dialog reuses
 * it) and not from `GetUserInfo` (every boot and token handoff runs it).
 */
export const rememberAccount = (
  userInfo: IUserInfoDto | undefined,
  method: RememberedSignInMethod,
  now: number = Date.now()
): void => {
  const email: string = userInfo?.username?.trim() ?? "";
  if (email.length === 0) {
    return;
  }

  const current: IStoreRead = readStore();
  if (current.isForeign) {
    return;
  }

  const key: string = normaliseRememberedEmail(email);
  const account: IRememberedAccount = {
    email,
    firstName: userInfo?.firstName?.trim() ?? "",
    lastName: userInfo?.lastName?.trim() ?? "",
    method,
    lastSignedInAt: now
  };
  const photoUrl: string | undefined = toPhotoUrl(userInfo?.profilePhotoUrl);
  if (photoUrl) {
    account.photoUrl = photoUrl;
  }
  const others: IRememberedAccount[] = current.accounts.filter(
    (existing: IRememberedAccount): boolean => normaliseRememberedEmail(existing.email) !== key
  );

  writeStore([account, ...others].slice(0, MAX_REMEMBERED_ACCOUNTS));
};

/**
 * Keeps an ALREADY remembered account's photo current — it never adds an entry and never reorders
 * the list, so unlike `rememberAccount` it is safe from every boot (`GetUserInfo`) and from the
 * profile dialog's photo upload/removal. A photo that is gone (empty or not an http(s) address)
 * removes the stored one, so the sign-in page falls back to initials instead of a dead image.
 */
export const syncRememberedAccountPhoto = (email: string | undefined, photoUrl: string | undefined): void => {
  const address: string = email?.trim() ?? "";
  if (address.length === 0) {
    return;
  }

  const current: IStoreRead = readStore();
  if (current.isForeign) {
    return;
  }

  const key: string = normaliseRememberedEmail(address);
  const nextPhotoUrl: string | undefined = toPhotoUrl(photoUrl);
  let isChanged: boolean = false;
  const accounts: IRememberedAccount[] = current.accounts.map((existing: IRememberedAccount): IRememberedAccount => {
    if (normaliseRememberedEmail(existing.email) !== key || existing.photoUrl === nextPhotoUrl) {
      return existing;
    }
    isChanged = true;
    const updated: IRememberedAccount = { ...existing, photoUrl: nextPhotoUrl };
    if (!nextPhotoUrl) {
      delete updated.photoUrl;
    }
    return updated;
  });

  if (isChanged) {
    writeStore(accounts);
  }
};

/** Removes one address from this device. Returns what is left, most recent first. */
export const forgetRememberedAccount = (email: string): IRememberedAccount[] => {
  const current: IStoreRead = readStore();
  if (current.isForeign) {
    return [];
  }

  const key: string = normaliseRememberedEmail(email);
  const remaining: IRememberedAccount[] = current.accounts.filter(
    (existing: IRememberedAccount): boolean => normaliseRememberedEmail(existing.email) !== key
  );
  writeStore(remaining);
  return remaining;
};

/** "Ayşe Demir", or the address when the account came without a name. */
export const getRememberedAccountName = (account: IRememberedAccount): string => {
  const name: string = `${account.firstName} ${account.lastName}`.trim();
  return name.length > 0 ? name : account.email;
};

/** "AD" — first letters of the first and last name, or of the address when there is no name. */
export const getRememberedAccountInitials = (account: IRememberedAccount): string => {
  const letters: string = [account.firstName, account.lastName].map((part: string): string => part.charAt(0)).join("");
  return (letters.length > 0 ? letters : account.email.charAt(0)).toUpperCase();
};
